<template>
  <div class="working-hours-form">
    <a-spin :spinning="loading" :tip="labels.form.loading">
      <ValidationObserver ref="observer">
        <a-form
          :model="form"
          :label-width="80"
          slot-scope="{ validate }"
          @submit.prevent="validate().then(handleSubmit)"
        >
          <a-row
            type="flex"
            justify="space-between"
            :gutter="16"
            :style="{ marginBottom: '15px' }"
          >
            <!-- Title -->
            <a-col :xs="24" :md="12">
              <h2 class="form-title">
                <a-icon :type="isEdit ? 'edit' : 'form'" class="form-icon" />
                <span>{{
                  isEdit
                    ? `${labels.form.titleView} número: ${form.code || ""}`
                    : labels.form.titleProcess
                }}</span>
              </h2>
            </a-col>
            <!-- Actions -->
            <a-col :xs="24" :md="12">
              <a-row
                type="flex"
                justify="end"
                :gutter="16"
                :style="{
                  alignItems: 'flex-end',
                  marginBottom: '0px',
                }"
              >
                <a-col>
                  <a-button-group>
                    <a-button
                      :type="isEdit ? 'primary' : 'danger'"
                      ghost
                      @click="handleCancel"
                      icon="arrow-left"
                    >
                      <span>{{ labels.form.goBack }}</span>
                    </a-button>
                    <a-button
                      v-if="canEdit"
                      type="primary"
                      :loading="submitting"
                      @click="validate().then(handleSubmit)"
                    >
                      <span>{{ labels.form.save }}</span>
                    </a-button>
                    <a-button
                      v-if="canEdit"
                      type="primary"
                      ghost
                      :loading="submitting"
                      @click="validate().then(() => handleSubmit(true))"
                    >
                      <span>{{ labels.form.saveAndClose }}</span>
                    </a-button>
                  </a-button-group>
                </a-col>
              </a-row>
            </a-col>
          </a-row>
          <a-row type="flex" justify="center">
            <a-col :span="24">
              <a-card>
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="24">
                    <SwitchInput
                      v-model="form.status"
                      :label="labels.form.status.label"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="24">
                    <Input
                      v-model="form.title"
                      :vid="labels.form.title.label"
                      :label="labels.form.title.label"
                      :name="labels.form.title.label"
                      :formitem="{ label: labels.form.title.label }"
                      :placeholder="labels.form.title.placeholder"
                      rules="required"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                  <a-col :span="24">
                    <TextArea
                      :label="labels.form.description.label"
                      :placeholder="labels.form.description.placeholder"
                      v-model="form.description"
                    />
                  </a-col>
                </a-row>

                <!-- MONDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.monday_enabled"
                      :label="labels.form.monday.label"
                      :name="labels.form.monday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.monday_start"
                      :label="labels.form.monday.start.label"
                      :placeholder="labels.form.monday.start.placeholder"
                      :rules="form.monday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.monday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.monday_end"
                      :label="labels.form.monday.end.label"
                      :placeholder="labels.form.monday.end.placeholder"
                      :rules="form.monday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.monday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- TUESDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.tuesday_enabled"
                      :label="labels.form.tuesday.label"
                      :name="labels.form.tuesday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.tuesday_start"
                      :label="labels.form.tuesday.start.label"
                      :placeholder="labels.form.tuesday.start.placeholder"
                      :rules="form.tuesday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.tuesday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.tuesday_end"
                      :label="labels.form.tuesday.end.label"
                      :placeholder="labels.form.tuesday.end.placeholder"
                      :rules="form.tuesday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.tuesday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- WEDNESDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.wednesday_enabled"
                      :label="labels.form.wednesday.label"
                      :name="labels.form.wednesday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.wednesday_start"
                      :label="labels.form.wednesday.start.label"
                      :placeholder="labels.form.wednesday.start.placeholder"
                      :rules="form.wednesday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.wednesday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.wednesday_end"
                      :label="labels.form.wednesday.end.label"
                      :placeholder="labels.form.wednesday.end.placeholder"
                      :rules="form.wednesday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.wednesday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- THURSDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.thursday_enabled"
                      :label="labels.form.thursday.label"
                      :name="labels.form.thursday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.thursday_start"
                      :label="labels.form.thursday.start.label"
                      :placeholder="labels.form.thursday.start.placeholder"
                      :rules="form.thursday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.thursday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.thursday_end"
                      :label="labels.form.thursday.end.label"
                      :placeholder="labels.form.thursday.end.placeholder"
                      :rules="form.thursday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.thursday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- FRIDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.friday_enabled"
                      :label="labels.form.friday.label"
                      :name="labels.form.friday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.friday_start"
                      :label="labels.form.friday.start.label"
                      :placeholder="labels.form.friday.start.placeholder"
                      :rules="form.friday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.friday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.friday_end"
                      :label="labels.form.friday.end.label"
                      :placeholder="labels.form.friday.end.placeholder"
                      :rules="form.friday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.friday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- SATURDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.saturday_enabled"
                      :label="labels.form.saturday.label"
                      :name="labels.form.saturday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.saturday_start"
                      :label="labels.form.saturday.start.label"
                      :placeholder="labels.form.saturday.start.placeholder"
                      :rules="form.saturday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.saturday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.saturday_end"
                      :label="labels.form.saturday.end.label"
                      :placeholder="labels.form.saturday.end.placeholder"
                      :rules="form.saturday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.saturday_enabled"
                    />
                  </a-col>
                </a-row>

                <!-- SUNDAY -->
                <a-row type="flex" justify="space-between" :gutter="24">
                  <a-col :xs="24" :sm="24" :md="8">
                    <CheckboxComponent
                      v-model="form.sunday_enabled"
                      :label="labels.form.sunday.label"
                      :name="labels.form.sunday.label"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.sunday_start"
                      :label="labels.form.sunday.start.label"
                      :placeholder="labels.form.sunday.start.placeholder"
                      :rules="form.sunday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.sunday_enabled"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <TimePicker
                      v-model="form.sunday_end"
                      :label="labels.form.sunday.end.label"
                      :placeholder="labels.form.sunday.end.placeholder"
                      :rules="form.sunday_enabled ? 'required' : ''"
                      use12Hours
                      :format="timeFormat"
                      :allowClear="true"
                      :disabled="!canEdit || !form.sunday_enabled"
                    />
                  </a-col>
                </a-row>
              </a-card>
            </a-col>
          </a-row>
        </a-form>
      </ValidationObserver>
    </a-spin>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import labels from "@/utils/labels";
import Input from "@/components/core/VeeValidateForm/Input.vue";
import TimePicker from "@/components/core/VeeValidateForm/TimePicker.vue";
import TextArea from "@/components/core/VeeValidateForm/TextArea.vue";
import CheckboxComponent from "@/components/core/VeeValidateForm/Checkbox.vue";
import SwitchInput from "@/components/core/VeeValidateForm/Switch.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import { mask } from "vue-the-mask";
import { adminPageTitle, hasRoles, roles } from "../../../utils/utils";
import moment from "moment-timezone";

const initialFormState = {
  id: null,
  title: "",
  description: "",
  monday_enabled: false,
  monday_start: null,
  monday_end: null,
  tuesday_enabled: false,
  tuesday_start: null,
  tuesday_end: null,
  wednesday_enabled: false,
  wednesday_start: null,
  wednesday_end: null,
  thursday_enabled: false,
  thursday_start: null,
  thursday_end: null,
  friday_enabled: false,
  friday_start: null,
  friday_end: null,
  saturday_enabled: false,
  saturday_start: null,
  saturday_end: null,
  sunday_enabled: false,
  sunday_start: null,
  sunday_end: null,
  status: true,
};

export default {
  name: "WorkingHourForm",
  directives: { themask: mask },
  components: {
    ValidationObserver,
    Input,
    TextArea,
    TimePicker,
    CheckboxComponent,
    SwitchInput,
  },
  metaInfo: {
    title: adminPageTitle(labels.workingHours.title),
    titleTemplate: () => {
      return `${adminPageTitle()} - ${labels.workingHours.pageTitleViewing}`;
    },
  },
  data() {
    return {
      labels: labels.workingHours,
      loading: false,
      submitting: false,
      type: undefined,
      form: _.cloneDeep(initialFormState),
    };
  },
  async created() {
    this.resetForm();
    if (this.$route.params.id) {
      this.fetchData();
    }
  },
  computed: {
    ...mapState("workingHours", ["workingHour"]),
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    isEdit() {
      return !!this.$route.params?.id;
    },
    canEdit() {
      const { accessRoles } = this.getLoggedUser;
      return (
        !this.isEdit ||
        hasRoles(accessRoles, [roles.admin.ROLE_WORKING_HOURS_UPDATE])
      );
    },
    timeFormat() {
      return "hh:mm A";
    },
    serverTimeFormat() {
      return "HH:mm:ss";
    },
    isMobile() {
      return this.screenWidth < 768;
    },
  },
  methods: {
    ...mapActions("workingHours", [
      "fetchWorkingHour",
      "updateWorkingHour",
      "resetForm",
    ]),
    async fetchData() {
      try {
        this.loading = true;
        await this.fetchWorkingHour(this.$route.params.id);
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
        this.$log.error(error);
        this.$router.push({ name: "admin.working-hours" });
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit(closeAfterSave = false) {
      if (this.submitting) return;
      this.submitting = true;
      let response = null;
      let editting = this.isEdit;
      if (!editting) {
        return;
      }
      try {
        const formData = _.cloneDeep(this.form);

        if (!formData.monday_enabled) {
          formData.monday_start = undefined;
          formData.monday_end = undefined;
        }
        if (!formData.tuesday_enabled) {
          formData.tuesday_start = undefined;
          formData.tuesday_end = undefined;
        }
        if (!formData.wednesday_enabled) {
          formData.wednesday_start = undefined;
          formData.wednesday_end = undefined;
        }
        if (!formData.thursday_enabled) {
          formData.thursday_start = undefined;
          formData.thursday_end = undefined;
        }
        if (!formData.friday_enabled) {
          formData.friday_start = undefined;
          formData.friday_end = undefined;
        }
        if (!formData.saturday_enabled) {
          formData.saturday_start = undefined;
          formData.saturday_end = undefined;
        }
        if (!formData.sunday_enabled) {
          formData.sunday_start = undefined;
          formData.sunday_end = undefined;
        }

        Object.keys(formData).forEach((key) => {
          if (
            formData[key] &&
            typeof formData[key] === "object" &&
            "format" in formData[key]
          ) {
            formData[key] = formData[key].format(this.serverTimeFormat);
          } else if (formData[key] === "") {
            formData[key] = null;
          }
        });

        response = await this.updateWorkingHour(formData);

        if (response) this.$message.success(response.message);
        if (closeAfterSave) {
          this.$router.push({ name: "admin.working-hours" });
        } else {
          if (!editting) {
            this.$router.push({
              name: "admin.working-hours.edit",
              params: { id: response.data.id },
            });
          }
        }
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
      } finally {
        this.submitting = false;
      }
    },
    handleCancel() {
      this.$router.push({ name: "admin.working-hours" });
    },
  },
  watch: {
    "$route.params.id"(value, oldValue) {
      if (value && value !== oldValue) {
        this.fetchData();
      }
    },
    workingHour: {
      handler(workingHour) {
        console.log("Working hour updated:", workingHour);
        Object.assign(
          this.form,
          workingHour
            ? {
                ..._.cloneDeep(workingHour),
                monday_enabled: !!workingHour.monday_enabled,
                tuesday_enabled: !!workingHour.tuesday_enabled,
                wednesday_enabled: !!workingHour.wednesday_enabled,
                thursday_enabled: !!workingHour.thursday_enabled,
                friday_enabled: !!workingHour.friday_enabled,
                saturday_enabled: !!workingHour.saturday_enabled,
                sunday_enabled: !!workingHour.sunday_enabled,
                monday_start: workingHour.monday_start
                  ? moment(workingHour.monday_start, this.serverTimeFormat)
                  : null,
                monday_end: workingHour.monday_end
                  ? moment(workingHour.monday_end, this.serverTimeFormat)
                  : null,
                tuesday_start: workingHour.tuesday_start
                  ? moment(workingHour.tuesday_start, this.serverTimeFormat)
                  : null,
                tuesday_end: workingHour.tuesday_end
                  ? moment(workingHour.tuesday_end, this.serverTimeFormat)
                  : null,
                wednesday_start: workingHour.wednesday_start
                  ? moment(workingHour.wednesday_start, this.serverTimeFormat)
                  : null,
                wednesday_end: workingHour.wednesday_end
                  ? moment(workingHour.wednesday_end, this.serverTimeFormat)
                  : null,
                thursday_start: workingHour.thursday_start
                  ? moment(workingHour.thursday_start, this.serverTimeFormat)
                  : null,
                thursday_end: workingHour.thursday_end
                  ? moment(workingHour.thursday_end, this.serverTimeFormat)
                  : null,
                friday_start: workingHour.friday_start
                  ? moment(workingHour.friday_start, this.serverTimeFormat)
                  : null,
                friday_end: workingHour.friday_end
                  ? moment(workingHour.friday_end, this.serverTimeFormat)
                  : null,
                saturday_start: workingHour.saturday_start
                  ? moment(workingHour.saturday_start, this.serverTimeFormat)
                  : null,
                saturday_end: workingHour.saturday_end
                  ? moment(workingHour.saturday_end, this.serverTimeFormat)
                  : null,
                sunday_start: workingHour.sunday_start
                  ? moment(workingHour.sunday_start, this.serverTimeFormat)
                  : null,
                sunday_end: workingHour.sunday_end
                  ? moment(workingHour.sunday_end, this.serverTimeFormat)
                  : null,
              }
            : _.cloneDeep(initialFormState)
        );
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.form = _.cloneDeep(initialFormState);
    this.resetForm();
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.working-hours-form {
  .form-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .form-icon {
    margin-right: 8px;
  }
  .form-button-group {
    margin-top: 16px;
  }
}
::v-deep {
  .ant-input[disabled],
  .ant-time-picker-input[disabled],
  .ant-input-number-disabled,
  .ant-select-disabled .ant-select-selection {
    background-color: color(--white) !important;
    color: rgba(0, 0, 0, 0.65) !important;
  }
}
</style>
